import { useEffect } from 'react';

// All 24 service area cities for LocalBusiness schema
const serviceAreaCities = [
  { name: "Seattle", county: "King" },
  { name: "Spokane", county: "Spokane" },
  { name: "Vancouver", county: "Clark" },
  { name: "Bellevue", county: "King" },
  { name: "Kent", county: "King" },
  { name: "Renton", county: "King" },
  { name: "Federal Way", county: "King" },
  { name: "Spokane Valley", county: "Spokane" },
  { name: "Kirkland", county: "King" },
  { name: "Yakima", county: "Yakima" },
  { name: "Kennewick", county: "Benton" },
  { name: "Auburn", county: "King/Pierce" },
  { name: "Pasco", county: "Franklin" },
  { name: "Marysville", county: "Snohomish" },
  { name: "Redmond", county: "King" },
  { name: "Shoreline", county: "King" },
  { name: "Sammamish", county: "King" },
  { name: "Lakewood", county: "Pierce" },
  { name: "Richland", county: "Benton" },
  { name: "Burien", county: "King" },
  { name: "Lacey", county: "Thurston" },
  { name: "Olympia", county: "Thurston" },
  { name: "Edmonds", county: "Snohomish" },
  { name: "Everett", county: "Snohomish" },
  { name: "Tacoma", county: "Pierce" },
  { name: "Bellingham", county: "Whatcom" },
  { name: "Mount Vernon", county: "Skagit" },
  { name: "Oak Harbor", county: "Island" }
];

const SEOHead = ({ 
  title,
  description,
  pageType = 'website',
  articleData = null,
  faqData = null,
  localArea = null,  // For city-specific pages: { city: "Everett", county: "Snohomish", state: "WA" }
  howToData = null,  // For HowTo schema: { name, description, steps: [{ name, text }] }
  speakableSelectors = null, // CSS selectors for speakable content: [".hero-title", ".main-content"]
  videoData = null,  // For VideoObject schema: [{ id, title, description, thumbnailUrl, uploadDate, duration }]
  serviceData = null, // For Service schema: { name, description, category }
  collectionData = null, // For CollectionPage + ItemList schema: { name, description, items: [{ name, url, description }] }
  image = null, // Featured image for Open Graph
  noindex = false // Set to true to prevent search engines from indexing (also disables JSON-LD)
}) => {
  const siteName = "Amy C. Brown Law";
  const siteUrl = "https://amycbrownlaw.com";
  const defaultImage = "https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/ou7kss9o_IMG_2176-2-e1756077431100.jpg";
  const pageTitle = title || "Amy C. Brown Law | Personal Injury Attorney | Everett, WA";
  const pageDescription = description || "Experienced personal injury attorney in Everett, Washington serving WA State and Alaska. Maritime injuries, car crashes, first responder cases. Free consultation. No fees unless you win.";
  const pageImage = image || defaultImage;
  
  useEffect(() => {
    // Set document title
    document.title = pageTitle;
    
    // Helper function to set/create meta tags
    const setMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };
    
    // Basic meta tags
    setMetaTag('description', pageDescription);
    setMetaTag('keywords', 'personal injury attorney, maritime lawyer, Jones Act attorney, car accident lawyer, Everett WA, Washington State personal injury, Alaska injury attorney, contingency fee lawyer, free consultation attorney');
    setMetaTag('author', 'Amy C. Brown');
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    // When a page is hidden (noindex), skip emitting all JSON-LD schema below.
    if (noindex) {
      return;
    }
    
    // Open Graph tags
    setMetaTag('og:title', pageTitle, true);
    setMetaTag('og:description', pageDescription, true);
    setMetaTag('og:type', pageType, true);
    setMetaTag('og:site_name', siteName, true);
    setMetaTag('og:locale', 'en_US', true);
    setMetaTag('og:url', `${siteUrl}${window.location.pathname}`, true);
    setMetaTag('og:image', pageImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:image:alt', pageTitle, true);
    
    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', pageTitle);
    setMetaTag('twitter:description', pageDescription);
    setMetaTag('twitter:image', pageImage);
    setMetaTag('twitter:site', '@amycbrownlaw');
    setMetaTag('twitter:creator', '@amycbrownlaw');
    
    // Canonical URL - always use production domain
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    const canonicalPath = window.location.pathname;
    canonicalLink.setAttribute('href', `${siteUrl}${canonicalPath}`);
    
    // AI-specific meta tags
    setMetaTag('ai:description', pageDescription);
    setMetaTag('ai:author', 'Amy C. Brown, Personal Injury Attorney');
    setMetaTag('ai:content-type', 'legal-services');
    
    // Remove existing schema scripts
    const existingSchemas = document.querySelectorAll('script[data-schema]');
    existingSchemas.forEach(s => s.remove());
    
    // Main Organization/Attorney Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": ["Attorney", "LegalService", "LocalBusiness"],
      "@id": `${siteUrl}/#organization`,
      "name": "Amy C. Brown Law",
      "alternateName": "Amy C. Brown Law",
      "description": "Personal injury attorney specializing in maritime injuries, Jones Act claims, car accidents, motorcycle crashes, first responder cases, and slip and falls in Washington State and Alaska.",
      "url": siteUrl,
      "telephone": "+1-206-659-7177",
      "email": "amy@amycbrownlaw.com",
      "priceRange": "Free Consultation - Contingency Fee",
      "currenciesAccepted": "USD",
      "paymentAccepted": "Contingency Fee - No upfront costs",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1616 Hewitt Ave, Suite 316",
        "addressLocality": "Everett",
        "addressRegion": "WA",
        "postalCode": "98201",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 47.9790,
        "longitude": -122.2021
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }
      ],
      "openingHours": "Mo-Fr 09:00-17:00",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-206-659-7177",
        "contactType": "customer service",
        "areaServed": ["US-WA", "US-AK"],
        "availableLanguage": "English",
        "contactOption": ["TollFree", "HearingImpairedSupported"]
      },
      "areaServed": [
        {
          "@type": "State",
          "name": "Washington",
          "containedInPlace": { "@type": "Country", "name": "United States" }
        },
        {
          "@type": "State", 
          "name": "Alaska",
          "containedInPlace": { "@type": "Country", "name": "United States" }
        },
        // All 24+ service area cities
        ...serviceAreaCities.map(city => ({
          "@type": "City",
          "name": city.name,
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": `${city.county} County, Washington`
          }
        }))
      ],
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 47.9790,
          "longitude": -122.2021
        },
        "geoRadius": "500 mi"
      },
      "founder": {
        "@type": "Person",
        "@id": `${siteUrl}/#attorney`,
        "name": "Amy C. Brown",
        "jobTitle": "Personal Injury Attorney",
        "description": "Personal injury attorney with over 10 years of experience specializing in maritime law, Jones Act claims, and personal injury cases in Washington and Alaska.",
        "knowsAbout": [
          { "@type": "Thing", "name": "Personal Injury", "sameAs": "https://en.wikipedia.org/wiki/Personal_injury" },
          { "@type": "Thing", "name": "Maritime Law", "sameAs": "https://en.wikipedia.org/wiki/Admiralty_law" },
          { "@type": "Thing", "name": "Jones Act", "sameAs": "https://en.wikipedia.org/wiki/Merchant_Marine_Act_of_1920" },
          { "@type": "Thing", "name": "Admiralty Law", "sameAs": "https://en.wikipedia.org/wiki/Admiralty_law" },
          { "@type": "Thing", "name": "Traffic Collision", "sameAs": "https://en.wikipedia.org/wiki/Traffic_collision" },
          { "@type": "Thing", "name": "Wrongful Death Claim", "sameAs": "https://en.wikipedia.org/wiki/Wrongful_death_claim" },
          { "@type": "Thing", "name": "Premises Liability", "sameAs": "https://en.wikipedia.org/wiki/Premises_liability" },
          { "@type": "Thing", "name": "Workers Compensation", "sameAs": "https://en.wikipedia.org/wiki/Workers%27_compensation" }
        ],
        "hasCredential": {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "Attorney License",
          "recognizedBy": [
            { "@type": "Organization", "name": "Washington State Bar Association" },
            { "@type": "Organization", "name": "Alaska Bar Association" }
          ]
        }
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Legal Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Maritime Injury Representation",
              "description": "Legal representation for injured seamen under the Jones Act, including maintenance and cure claims and unseaworthiness actions."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Car Accident Representation",
              "description": "Legal representation for victims of automobile accidents in Washington and Alaska."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Motorcycle Crash Representation", 
              "description": "Specialized legal representation for motorcycle accident victims."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "First Responder Injury Claims",
              "description": "Legal representation for police officers and first responders injured in vehicle crashes."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Slip and Fall Cases",
              "description": "Premises liability representation for slip and fall injuries caused by property owner negligence."
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "23",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "John H." },
          "datePublished": "2025-12-01",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "reviewBody": "Amy helped me with my auto accident. She was very professional and got me the best outcome that could have happened in this situation. I highly recommend her for auto/injury litigation."
        }
      ],
      "award": [
        "10 Best Attorney 2025 - Client Satisfaction",
        "Top 10 Personal Injury Attorney 2025 - Attorney and Practice Magazine"
      ],
      "sameAs": [
        "https://www.linkedin.com/company/amy-c-brown-law-pllc/",
        "https://www.linkedin.com/in/amy-brown-736a9b11/",
        "https://www.youtube.com/@amycbrownlaw",
        "https://www.google.com/maps/place/Amy+C.+Brown+Law",
        "https://www.avvo.com/attorneys/98201-wa-amy-brown-4585008.html",
        "https://www.mywsba.org/PersonifyEbusiness/LegalDirectory/LegalProfile.aspx?Usr_ID=000000047430",
        "https://www.martindale.com/attorney/amy-c-brown-169137008/"
      ],
      "knowsAbout": [
        { "@type": "Thing", "name": "Personal Injury", "sameAs": "https://en.wikipedia.org/wiki/Personal_injury" },
        { "@type": "Thing", "name": "Maritime Law", "sameAs": "https://en.wikipedia.org/wiki/Admiralty_law" },
        { "@type": "Thing", "name": "Jones Act", "sameAs": "https://en.wikipedia.org/wiki/Merchant_Marine_Act_of_1920" },
        { "@type": "Thing", "name": "Admiralty Law", "sameAs": "https://en.wikipedia.org/wiki/Admiralty_law" },
        { "@type": "Thing", "name": "Traffic Collision", "sameAs": "https://en.wikipedia.org/wiki/Traffic_collision" },
        { "@type": "Thing", "name": "Washington Law", "sameAs": "https://en.wikipedia.org/wiki/Law_of_Washington_(state)" },
        { "@type": "Thing", "name": "Contingent Fee", "sameAs": "https://en.wikipedia.org/wiki/Contingent_fee" }
      ],
      "slogan": "Your Voice + My Experience = One Strong Case"
    };
    
    // Person Schema for Amy C. Brown (helps AI entity recognition)
    const personSchema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": `${siteUrl}/#amy-c-brown`,
      "name": "Amy C. Brown",
      "givenName": "Amy",
      "familyName": "Brown",
      "honorificSuffix": "Esq.",
      "jobTitle": "Personal Injury Attorney",
      "description": "Personal injury attorney specializing in maritime injuries, Jones Act claims, car accidents, and first responder cases in Washington State and Alaska.",
      "image": "https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/ou7kss9o_IMG_2176-2-e1756077431100.jpg",
      "url": `${siteUrl}/about`,
      "email": "amy@amycbrownlaw.com",
      "telephone": "+1-206-659-7177",
      "worksFor": { "@id": `${siteUrl}/#organization` },
      "alumniOf": {
        "@type": "CollegeOrUniversity",
        "name": "Seattle University School of Law"
      },
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "Bar Admission",
          "name": "WSBA Member #47430",
          "identifier": "47430",
          "recognizedBy": {
            "@type": "Organization",
            "name": "Washington State Bar Association",
            "url": "https://www.wsba.org"
          },
          "url": "https://www.mywsba.org/PersonifyEbusiness/LegalDirectory/LegalProfile.aspx?Usr_ID=000000047430",
          "dateCreated": "2013"
        },
        {
          "@type": "EducationalOccupationalCredential", 
          "credentialCategory": "Bar Admission",
          "recognizedBy": { "@type": "Organization", "name": "Alaska Bar Association" }
        }
      ],
      "knowsAbout": [
        { "@type": "Thing", "name": "Personal Injury", "sameAs": "https://en.wikipedia.org/wiki/Personal_injury" },
        { "@type": "Thing", "name": "Maritime Law", "sameAs": "https://en.wikipedia.org/wiki/Admiralty_law" },
        { "@type": "Thing", "name": "Jones Act", "sameAs": "https://en.wikipedia.org/wiki/Merchant_Marine_Act_of_1920" },
        { "@type": "Thing", "name": "Traffic Collision", "sameAs": "https://en.wikipedia.org/wiki/Traffic_collision" }
      ],
      "sameAs": [
        "https://www.linkedin.com/in/amy-brown-736a9b11/",
        "https://www.youtube.com/@amycbrownlaw",
        "https://www.avvo.com/attorneys/98201-wa-amy-brown-4585008.html",
        "https://www.mywsba.org/PersonifyEbusiness/LegalDirectory/LegalProfile.aspx?Usr_ID=000000047430"
      ]
    };
    
    // Website Schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      "name": siteName,
      "url": siteUrl,
      "description": pageDescription,
      "publisher": { "@id": `${siteUrl}/#organization` },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${siteUrl}/blog?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };
    
    // Note: Breadcrumb schema is handled by Breadcrumbs.js component
    
    // Add schemas to page
    const addSchema = (schema, id) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', id);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };
    
    addSchema(organizationSchema, 'organization');
    addSchema(websiteSchema, 'website');
    addSchema(personSchema, 'person');
    
    // FAQ Schema if provided
    if (faqData && faqData.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      addSchema(faqSchema, 'faq');
    }
    
    // Local Service Schema for city-specific pages
    if (localArea) {
      const localServiceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Personal Injury Legal Services",
        "provider": {
          "@type": "Attorney",
          "name": "Amy C. Brown",
          "telephone": "(206) 659-7177",
          "priceRange": "Contingency Fee - No Win, No Fee"
        },
        "areaServed": {
          "@type": "City",
          "name": localArea.city,
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": `${localArea.county} County, ${localArea.state}`
          }
        },
        "description": pageDescription,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Free consultation. Contingency fee - no fees unless you win."
        }
      };
      addSchema(localServiceSchema, 'local-service');
    }
    
    // Article Schema if provided
    if (articleData) {
      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": articleData.title,
        "description": articleData.description,
        "author": {
          "@type": "Person",
          "name": "Amy C. Brown",
          "@id": `${siteUrl}/#attorney`,
          "url": `${siteUrl}/about-amy`
        },
        "publisher": { "@id": `${siteUrl}/#organization` },
        "datePublished": articleData.datePublished,
        "dateModified": articleData.dateModified || articleData.datePublished,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${siteUrl}${window.location.pathname}`
        },
        "image": pageImage,
        ...(articleData.articleSection && { "articleSection": articleData.articleSection }),
        ...(articleData.keywords && { "keywords": articleData.keywords }),
        ...(articleData.wordCount && { "wordCount": articleData.wordCount }),
        ...(articleData.about && { "about": articleData.about })
      };
      addSchema(articleSchema, 'article');
    }
    
    // HowTo Schema for step-by-step guides
    if (howToData) {
      const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": howToData.name,
        "description": howToData.description,
        "totalTime": howToData.totalTime || "PT5M",
        "step": howToData.steps.map((step, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": step.name,
          "text": step.text,
          "url": step.url || `${siteUrl}#step-${index + 1}`
        }))
      };
      addSchema(howToSchema, 'howto');
    }
    
    // Speakable Schema for voice search optimization
    // Applied with custom selectors if provided, otherwise default selectors that
    // exist on most pages (h1, hero text, FAQ answers).
    const effectiveSpeakableSelectors = speakableSelectors && speakableSelectors.length > 0
      ? speakableSelectors
      : ['h1', '.lead', '[data-speakable]'];
    {
      const speakableSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": pageTitle,
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": effectiveSpeakableSelectors
        },
        "url": `${siteUrl}${window.location.pathname}`
      };
      addSchema(speakableSchema, 'speakable');
    }
    
    // VideoObject Schema for video content
    if (videoData && videoData.length > 0) {
      videoData.forEach((video, index) => {
        const videoSchema = {
          "@context": "https://schema.org",
          "@type": "VideoObject",
          "name": video.title,
          "description": video.description || `Legal information video by Amy C. Brown, personal injury attorney`,
          "thumbnailUrl": video.thumbnailUrl || `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`,
          "uploadDate": video.uploadDate || "2024-01-01T00:00:00Z",
          "duration": video.duration || "PT5M",
          "contentUrl": `https://www.youtube.com/watch?v=${video.id}`,
          "embedUrl": `https://www.youtube.com/embed/${video.id}`,
          "publisher": {
            "@type": "Organization",
            "name": "Amy C. Brown Law",
            "logo": {
              "@type": "ImageObject",
              "url": `${siteUrl}/logo.png`
            }
          },
          "author": {
            "@type": "Person",
            "name": "Amy C. Brown"
          }
        };
        addSchema(videoSchema, `video-${index}`);
      });
    }
    
    // Service Schema for practice area pages
    if (serviceData) {
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "name": serviceData.name,
        "description": serviceData.description,
        "serviceType": serviceData.category || "Personal Injury Legal Services",
        "provider": {
          "@type": "Attorney",
          "@id": `${siteUrl}/#attorney`,
          "name": "Amy C. Brown",
          "telephone": "+1-206-659-7177",
          "email": "amy@amycbrownlaw.com"
        },
        "areaServed": [
          { "@type": "State", "name": "Washington" },
          { "@type": "State", "name": "Alaska" }
        ],
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Free consultation. Contingency fee - no fees unless you win.",
          "availability": "https://schema.org/InStock"
        },
        "termsOfService": `${siteUrl}/terms-of-service`,
        "url": `${siteUrl}${window.location.pathname}`
      };
      addSchema(serviceSchema, 'service');
    }

    // CollectionPage + ItemList schema for hub/index pages (e.g., Case Results)
    if (collectionData) {
      const collectionSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": collectionData.name,
        "description": collectionData.description,
        "url": `${siteUrl}${window.location.pathname}`,
        "isPartOf": { "@id": `${siteUrl}/#website` },
        "publisher": { "@id": `${siteUrl}/#organization` },
        "mainEntity": {
          "@type": "ItemList",
          "numberOfItems": collectionData.items.length,
          "itemListElement": collectionData.items.map((item, idx) => ({
            "@type": "ListItem",
            "position": idx + 1,
            "url": `${siteUrl}${item.url}`,
            "name": item.name,
            ...(item.description && { "description": item.description })
          }))
        }
      };
      addSchema(collectionSchema, 'collection');
    }
    
    // Cleanup
    return () => {
      const schemas = document.querySelectorAll('script[data-schema]');
      schemas.forEach(s => s.remove());
      // Note: We don't remove canonical as it should persist for the page
    };
  }, [pageTitle, pageDescription, pageType, pageImage, articleData, faqData, localArea, howToData, speakableSelectors, videoData, serviceData, collectionData, noindex]);
  
  return null;
};

export default SEOHead;
